import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { io, Socket } from 'socket.io-client'
import { api } from '../api/client'
import { getAccessToken } from '../shared/auth/token'

// Matches a real Mongo ObjectId (server-persisted notification), as opposed
// to a locally-generated `notif-<timestamp>-<rand>` id from addNotification —
// only the former exist server-side and can be marked read there.
const isPersistedId = (id: string) => /^[a-f0-9]{24}$/i.test(id)

interface Notification {
  id: string
  type: string
  title: string
  message?: string
  entityType?: string
  entityId?: string
  actionUrl?: string
  isRead: boolean
  createdAt: string
}

interface NotificationContextType {
  notifications: Notification[]
  unreadCount: number
  socket: Socket | null
  connected: boolean
  chimeMuted: boolean
  toggleChime: () => void
  connect: (organizationId: string) => void
  disconnect: () => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  /** Hide one notification from the panel (the server keeps the record). */
  dismissNotification: (id: string) => void
  /** Hide every notification from the panel (the server keeps the records). */
  clearAllNotifications: () => void
  addNotification: (notification: {
    title: string
    message?: string
    type?: string
    actionUrl?: string
    entityType?: string
    entityId?: string
  }) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

// Synthesizes a short two-tone chime with the Web Audio API rather than
// shipping/hosting an audio asset just for this.
function playChime() {
  try {
    const AudioCtxCtor = window.AudioContext || (window as any).webkitAudioContext
    if (!AudioCtxCtor) return
    const ctx: AudioContext = new AudioCtxCtor()
    const now = ctx.currentTime
    ;[880, 1318.5].forEach((freq, i) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      const start = now + i * 0.09
      gain.gain.setValueAtTime(0, start)
      gain.gain.linearRampToValueAtTime(0.18, start + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.35)
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start(start)
      osc.stop(start + 0.35)
    })
    setTimeout(() => ctx.close().catch(() => {}), 500)
  } catch {}
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>(() => {
    try {
      const saved = localStorage.getItem('goodday_app_notifications')
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })
  const [socket, setSocket] = useState<Socket | null>(null)
  const [connected, setConnected] = useState(false)
  const [chimeMuted, setChimeMuted] = useState<boolean>(() => {
    try {
      return localStorage.getItem('goodday_chime_muted') === 'true'
    } catch {
      return false
    }
  })
  // Suppresses the chime for the initial batch loaded from localStorage/the
  // server on connect — only notifications that arrive afterwards should ring.
  const hasLoadedRef = React.useRef(false)
  const chimeMutedRef = React.useRef(chimeMuted)
  chimeMutedRef.current = chimeMuted

  useEffect(() => {
    hasLoadedRef.current = true
  }, [])

  const toggleChime = () => {
    setChimeMuted((prev) => {
      const next = !prev
      try {
        localStorage.setItem('goodday_chime_muted', String(next))
      } catch {}
      return next
    })
  }

  // Listen to custom notification events dispatched from modals & components
  useEffect(() => {
    const handleCustomNotif = (e: any) => {
      if (e.detail) {
        addNotification(e.detail)
      }
    }
    window.addEventListener('goodday-lead-notification' as any, handleCustomNotif)
    return () => {
      window.removeEventListener('goodday-lead-notification' as any, handleCustomNotif)
    }
  }, [])

  const saveToStorage = (items: Notification[]) => {
    try {
      localStorage.setItem('goodday_app_notifications', JSON.stringify(items.slice(0, 50)))
    } catch {}
  }

  const addNotification = (notification: {
    title: string
    message?: string
    type?: string
    actionUrl?: string
    entityType?: string
    entityId?: string
  }) => {
    const newNotif: Notification = {
      id: `notif-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      title: notification.title,
      message: notification.message,
      type: notification.type || 'info',
      actionUrl: notification.actionUrl,
      entityType: notification.entityType,
      entityId: notification.entityId,
      isRead: false,
      createdAt: new Date().toISOString(),
    }
    setNotifications((prev) => {
      // Deduplicate: if a notification with the same title + entityType + entityId
      // (or title + message when there are no entity fields) was already added in
      // the last 5 seconds (e.g. from a socket broadcast of the same event),
      // don't add another copy.
      const now = Date.now()
      const alreadyExists = prev.some(n =>
        (n.title === newNotif.title &&
         n.entityType === newNotif.entityType &&
         n.entityId === newNotif.entityId &&
         (now - new Date(n.createdAt).getTime()) < 5000) ||
        (n.title === newNotif.title &&
         n.message === newNotif.message &&
         !newNotif.entityType && !newNotif.entityId &&
         (now - new Date(n.createdAt).getTime()) < 5000)
      )
      if (alreadyExists) return prev
      const updated = [newNotif, ...prev]
      saveToStorage(updated)
      return updated
    })
    if (hasLoadedRef.current && !chimeMutedRef.current) playChime()

    // Persist to server
    api.post('/notifications', {
      title: notification.title,
      message: notification.message,
      type: notification.type || 'info',
      actionUrl: notification.actionUrl,
      entityType: notification.entityType,
      entityId: notification.entityId,
    }, { skipErrorToast: true }).catch(() => {})
  }

  // Loads notification history from the server — the source of truth for
  // anything that fired while this browser was offline. localStorage still
  // seeds the initial state above so the panel doesn't flash empty while
  // this request is in flight.
  const fetchHistory = async () => {
    try {
      const res = await api.get<any>('/notifications', undefined, { skipErrorToast: true })
      const raw = Array.isArray(res) ? res : (res?.data || [])
      const serverItems: Notification[] = raw.map((n: any) => ({
        id: String(n._id || n.id),
        type: n.type || 'info',
        title: n.title,
        message: n.message,
        entityType: n.entityType,
        entityId: n.entityId,
        actionUrl: n.actionUrl,
        isRead: !!n.isRead,
        createdAt: n.createdAt || new Date().toISOString(),
      }))
      // Merge server history with existing local state — keep the local copy
      // if one already exists for the same event (title + entityType + entityId
      // match, or title + message when there are no entity fields).
      setNotifications((prevAll) => {
        // The server is the truth for saved notifications: one that is no longer
        // listed there (deleted here or on another device) is dropped from the local copy too.
        const serverIds = new Set(serverItems.map(n => n.id))
        const prev = prevAll.filter(n => !isPersistedId(n.id) || serverIds.has(n.id))
        const seen = new Set(prev.map(n =>
          `${n.title}|${n.entityType || ''}|${n.entityId || ''}|${n.message || ''}`
        ))
        const merged = [
          ...serverItems.filter(s => {
            const key = `${s.title}|${s.entityType || ''}|${s.entityId || ''}|${s.message || ''}`
            return !seen.has(key)
          }),
          ...prev,
        ]
        saveToStorage(merged)
        return merged
      })
    } catch {}
  }

  const connect = (organizationId: string) => {
    fetchHistory()

    if (socket?.connected) return

    const wsUrl = import.meta.env.VITE_WS_URL || import.meta.env.VITE_API_URL || 'http://localhost:4001'
    const newSocket = io(wsUrl, {
      auth: { token: getAccessToken() },
      transports: ['websocket', 'polling'],
    })

    newSocket.on('connect', () => {
      setConnected(true)
      // The server auto-joins this socket's own user room for notifications;
      // this join is only for the org-wide task/comment broadcast rooms.
      newSocket.emit('goodday:joinOrganization', organizationId)
    })

    newSocket.on('disconnect', () => setConnected(false))
    // Screens that refresh on live events (biometric punches, device commands) listen on this socket.
    ;(window as unknown as { __zynxSocket?: Socket }).__zynxSocket = newSocket

    const handleIncoming = (notification: Notification) => {
      setNotifications((prev) => {
        const now = Date.now()
        const isDup = prev.some(n =>
          (n.title === notification.title &&
           n.entityType === notification.entityType &&
           n.entityId === notification.entityId &&
           (now - new Date(n.createdAt).getTime()) < 5000) ||
          (n.title === notification.title &&
           n.message === notification.message &&
           !notification.entityType && !notification.entityId &&
           (now - new Date(n.createdAt).getTime()) < 5000)
        )
        if (isDup) return prev
        const updated = [notification, ...prev]
        saveToStorage(updated)
        return updated
      })
      if (hasLoadedRef.current && !chimeMutedRef.current) playChime()
    }

    newSocket.on('notification', handleIncoming)
    newSocket.on('goodday:notification', handleIncoming)

    newSocket.on('notifications', (notifs: Notification[]) => {
      setNotifications(notifs)
      saveToStorage(notifs)
    })

    setSocket(newSocket)
  }

  const disconnect = () => {
    delete (window as unknown as { __zynxSocket?: Socket }).__zynxSocket
    socket?.disconnect()
    setSocket(null)
    setConnected(false)
  }

  const markAsRead = (id: string) => {
    setNotifications(prev => {
      const updated = prev.map(n => n.id === id ? { ...n, isRead: true } : n)
      saveToStorage(updated)
      return updated
    })
    if (isPersistedId(id)) {
      api.post(`/notifications/${id}/read`, undefined, { skipErrorToast: true }).catch(() => {})
    }
  }

  const markAllAsRead = () => {
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, isRead: true }))
      saveToStorage(updated)
      return updated
    })
    api.post('/notifications/mark-all-read', undefined, { skipErrorToast: true }).catch(() => {})
  }

  const dismissNotification = (id: string) => {
    setNotifications(prev => {
      const updated = prev.filter(n => n.id !== id)
      saveToStorage(updated)
      return updated
    })
    if (isPersistedId(id)) {
      api.delete(`/notifications/${id}`, { skipErrorToast: true }).catch(() => {})
    }
  }

  const clearAllNotifications = () => {
    setNotifications([])
    saveToStorage([])
    api.post('/notifications/clear-all', undefined, { skipErrorToast: true }).catch(() => {})
  }

  const unreadCount = notifications.filter(n => !n.isRead).length

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      socket,
      connected,
      chimeMuted,
      toggleChime,
      connect,
      disconnect,
      markAsRead,
      markAllAsRead,
      dismissNotification,
      clearAllNotifications,
      addNotification,
    }}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

export type { Notification, Notification as NotificationItem }